/* global __initial_auth_token */
import React, { useState, useEffect, useRef } from 'react';
import { signInAnonymously, onAuthStateChanged, GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, signInWithPopup, signOut, signInWithCustomToken, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { collection, onSnapshot, doc, setDoc, addDoc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';

import { auth, db, storage, appId } from './services/firebase';
import { extractDataFromDocument } from './services/ocrService';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from './utils';

import SidebarItem from './components/SidebarItem';
import UserManagementView from './views/UserManagementView';
import DashboardView from './views/DashboardView';
import CasesView from './views/CasesView';
import ClientsView from './views/ClientsView';
import ClientDetailView from './views/ClientDetailView';
import TasksView from './views/TasksView';
import HearingsView from './views/HearingsView';
import HearingEditModal from './modals/HearingEditModal';
import ChatView from './views/ChatView';
import DocumentViewerModal from './modals/DocumentViewerModal';
import CaseDetailView from './views/CaseDetailView';
import NetHamishpatWizard from './modals/NetHamishpatWizard';
import FileConverterView from './views/FileConverterView';
import PrivateAIAgent from './components/PrivateAIAgent';
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeCase, setActiveCase] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  
  const [netWizardCase, setNetWizardCase] = useState(null);
  const [editingHearing, setEditingHearing] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);

  const [prefilledClientForCase, setPrefilledClientForCase] = useState('');

  const [loginError, setLoginError] = useState('');
  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [fullNameInput, setFullNameInput] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Clear client cache once to purge any previous mock/local data
  useEffect(() => {
    if (!localStorage.getItem('lawfirm_fresh_clean_v1')) {
      localStorage.clear();
      localStorage.setItem('lawfirm_fresh_clean_v1', 'true');
    }
  }, []);
  const [globalSearch, setGlobalSearch] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [firmUsers, setFirmUsers] = useState([]);
  const [firmSettings, setFirmSettings] = useState({ firmName: "משרד עורכי דין" });
  const [localFirmName, setLocalFirmName] = useState('');
  const [isDbReady, setIsDbReady] = useState(false);
  const [localFallbackActive, setLocalFallbackActive] = useState({});

  const [cases, setCases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCaseId = params.get('caseId');
    if (urlCaseId && cases.length > 0 && isDbReady) {
      const foundCase = cases.find(c => c._id === urlCaseId);
      if (foundCase) {
        setActiveCase(foundCase);
        setActiveTab('cases');
      }
    }
  }, [cases, isDbReady]);

  // Safety net: if nothing sets isDbReady within 5 seconds, force it so SSO buttons appear
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      setIsDbReady(prev => {
        if (!prev) console.warn('Safety timeout: forcing login buttons to appear');
        return true;
      });
    }, 5000);
    return () => clearTimeout(safetyTimeout);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.error("Firebase Auth Error:", error);
        // Auth failed - still show login buttons
        setIsDbReady(true);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  const saveLocal = (col, data, firestoreId = null) => {
    const localKey = 'lawfirm_' + col;
    let current = [];
    try {
      current = JSON.parse(localStorage.getItem(localKey)) || [];
    } catch(e) {}
    
    let updated;
    const targetId = firestoreId || data._id;
    
    if (targetId) {
      const exists = current.some(item => item._id === targetId);
      if (exists) {
        updated = current.map(item => item._id === targetId ? { ...item, ...data } : item);
      } else {
        updated = [...current, { ...data, _id: targetId }];
      }
    } else {
      const newId = 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      updated = [...current, { ...data, _id: newId }];
    }
    
    localStorage.setItem(localKey, JSON.stringify(updated));
    
    if (col === 'cases') setCases(updated);
    else if (col === 'tasks') setTasks(updated);
    else if (col === 'documents') setDocuments(updated);
    else if (col === 'hearings') setHearings(updated);
    else if (col === 'clients') setClients(updated);
    else if (col === 'messages') setMessages(updated);
    else if (col === 'firmUsers') {
      setFirmUsers(updated);
      if (currentUserProfile && data.email && data.email.toLowerCase() === currentUserProfile.email.toLowerCase()) {
        setCurrentUserProfile({ ...currentUserProfile, ...data, _id: targetId });
      }
    }
    else if (col === 'settings') {
      if (updated.length > 0) {
        setFirmSettings(updated[0]);
        setLocalFirmName(updated[0].firmName || '');
      }
    }
  };

  const deleteLocal = (col, firestoreId) => {
    const localKey = 'lawfirm_' + col;
    let current = [];
    try {
      current = JSON.parse(localStorage.getItem(localKey)) || [];
    } catch(e) {}
    
    const updated = current.filter(item => item._id !== firestoreId);
    localStorage.setItem(localKey, JSON.stringify(updated));
    
    if (col === 'cases') setCases(updated);
    else if (col === 'tasks') setTasks(updated);
    else if (col === 'documents') setDocuments(updated);
    else if (col === 'hearings') setHearings(updated);
    else if (col === 'clients') setClients(updated);
    else if (col === 'messages') setMessages(updated);
    else if (col === 'firmUsers') setFirmUsers(updated);
  };

  useEffect(() => {
    if (!user) return;
    let isFirstFetch = true;

    // Timeout fallback: if Firestore doesn't respond within 8 seconds, show login buttons anyway
    const dbTimeout = setTimeout(() => {
      if (isFirstFetch) {
        console.warn('Firestore timeout - showing login buttons as fallback');
        setIsDbReady(true);
        isFirstFetch = false;
      }
    }, 8000);

    const subscribeToCollection = (colName, setter) => {
      const colRef = collection(db, 'artifacts', appId, 'public', 'data', colName);
      return onSnapshot(colRef, (snapshot) => {
        const items = snapshot.docs.map(doc => ({ ...doc.data(), _id: doc.id }));
        setter(items);
        localStorage.setItem('lawfirm_' + colName, JSON.stringify(items));
        
        if (colName === 'firmUsers') {
          if (user && !user.isAnonymous && user.email) {
            const existingUser = items.find(u => u.email.toLowerCase() === user.email.toLowerCase());
            if (existingUser) {
              setCurrentUserProfile(existingUser);
            }
          }
        }

        if (colName === 'firmUsers' && isFirstFetch) {
          clearTimeout(dbTimeout);
          setIsDbReady(true);
          isFirstFetch = false;

          if (!user.isAnonymous && user.email) {
            const existingUser = items.find(u => u.email.toLowerCase() === user.email.toLowerCase());
            if (existingUser) {
              setCurrentUserProfile(existingUser);
              setIsAuthenticated(true);
            } else if (items.length === 0) {
              const newUser = { name: user.displayName || 'עורך דין ראשי', email: user.email.toLowerCase(), role: 'admin' };
              addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'firmUsers'), newUser).then((docRef) => {
                setCurrentUserProfile({ _id: docRef.id, ...newUser });
                setIsAuthenticated(true);
              });
            } else {
              signOut(auth).then(() => signInAnonymously(auth));
              setLoginError("הגישה נדחתה: כתובת הדוא\"ל שלך אינה מורשית. אנא פנה למנהל המשרד להוספתך.");
            }
          }
        }
      }, (error) => {
        console.warn(`Error fetching ${colName}, falling back to localStorage:`, error);
        setLocalFallbackActive(prev => ({ ...prev, [colName]: true }));
        
        const localKey = 'lawfirm_' + colName;
        let localData = [];
        try {
          localData = JSON.parse(localStorage.getItem(localKey)) || [];
        } catch(e) {}
        
        if (colName === 'settings' && localData.length === 0) {
          localData = [{ _id: 'main', firmName: "משרד עורכי דין" }];
        }
        
        setter(localData);
        
        if (colName === 'settings') {
          if (localData.length > 0) {
            setFirmSettings(localData[0]);
            setLocalFirmName(localData[0].firmName || '');
          }
        }
        
        if (colName === 'firmUsers') {
          if (user && !user.isAnonymous && user.email) {
            const existingUser = localData.find(u => u.email.toLowerCase() === user.email.toLowerCase());
            if (existingUser) {
              setCurrentUserProfile(existingUser);
            }
          }
        }

        if (colName === 'firmUsers' && isFirstFetch) {
          clearTimeout(dbTimeout);
          setIsDbReady(true);
          isFirstFetch = false;

          if (user && !user.isAnonymous && user.email) {
            const existingUser = localData.find(u => u.email.toLowerCase() === user.email.toLowerCase());
            if (existingUser) {
              setCurrentUserProfile(existingUser);
              setIsAuthenticated(true);
            } else {
              const targetId = 'local_' + Date.now();
              const newUser = { _id: targetId, name: user.displayName || 'עורך דין ראשי', email: user.email.toLowerCase(), role: 'admin' };
              const updatedUsers = [...localData, newUser];
              localStorage.setItem('lawfirm_firmUsers', JSON.stringify(updatedUsers));
              setFirmUsers(updatedUsers);
              setCurrentUserProfile(newUser);
              setIsAuthenticated(true);
            }
          }
        }
      });
    };

    const unsubCases = subscribeToCollection('cases', setCases);
    const unsubTasks = subscribeToCollection('tasks', setTasks);
    const unsubDocs = subscribeToCollection('documents', setDocuments);
    const unsubHearings = subscribeToCollection('hearings', setHearings);
    const unsubClients = subscribeToCollection('clients', setClients);
    const unsubUsers = subscribeToCollection('firmUsers', setFirmUsers);
    const unsubMessages = subscribeToCollection('messages', setMessages);
    const unsubSettings = subscribeToCollection('settings', (items) => {
      if (items.length > 0) {
        setFirmSettings(items[0]);
        setLocalFirmName(items[0].firmName || '');
      } else {
        setLocalFirmName("משרד עורכי דין");
      }
    });

    return () => {
      clearTimeout(dbTimeout);
      unsubCases(); unsubTasks(); unsubDocs(); unsubHearings(); unsubClients(); unsubUsers(); unsubSettings(); unsubMessages();
    };
  }, [user]);

  const dbOps = {
    handleSave: async (col, data, firestoreId = null) => {
      const { _id, ...saveData } = data; 
      const targetId = firestoreId || _id || data._id;
      
      saveLocal(col, data, targetId);
      
      try {
        if (auth.currentUser) {
          if (targetId) {
            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', col, targetId), saveData, { merge: true });
          } else {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', col), saveData);
          }
        } else {
          console.warn("No authenticated Firebase user, saved locally only.");
        }
      } catch (e) {
        console.warn("Firestore save failed, using local fallback state:", e);
      }
    },
    handleDelete: async (col, firestoreId) => {
      if (!window.confirm("האם אתה בטוח שברצונך למחוק? פעולה זו אינה הפיכה ותשפיע על כלל המערכת.")) return;
      
      deleteLocal(col, firestoreId);
      
      try {
        if (auth.currentUser) {
          await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', col, firestoreId));
        } else {
          console.warn("No authenticated Firebase user, deleted locally only.");
        }
      } catch (e) {
        console.warn("Firestore delete failed, using local fallback state:", e);
      }
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoginError('');
    setAuthLoading(true);

    const email = emailInput.trim().toLowerCase();
    const password = passwordInput;
    const name = fullNameInput.trim();

    try {
      if (authMode === 'signup') {
        if (!email || !password || !name) {
          throw new Error("אנא מלא את כל השדות.");
        }
        if (password.length < 6) {
          throw new Error("הסיסמה חייבת להכיל לפחות 6 תווים.");
        }

        // Check authorization in existing firm users list unless database is empty (first user bootstrap)
        if (firmUsers.length > 0) {
          const isAuthorized = firmUsers.some(u => u.email.toLowerCase() === email);
          if (!isAuthorized) {
            throw new Error("הגישה נדחתה: כתובת הדוא\"ל שלך אינה מורשית במערכת. אנא פנה למנהל המשרד להוספתך.");
          }
        }

        const result = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(result.user, { displayName: name });
      } else {
        if (!email || !password) {
          throw new Error("אנא מלא את כל השדות.");
        }
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      console.error("Authentication Error:", error);
      let errorMsg = error.message;
      if (error.code === 'auth/email-already-in-use') {
        errorMsg = "כתובת האימייל כבר נמצאת בשימוש.";
      } else if (error.code === 'auth/invalid-email') {
        errorMsg = "כתובת האימייל אינה תקינה.";
      } else if (error.code === 'auth/weak-password') {
        errorMsg = "הסיסמה חלשה מדי. עליה להכיל לפחות 6 תווים.";
      } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMsg = "שם המשתמש או הסיסמה אינם נכונים.";
      }
      setLoginError(errorMsg);
    } finally {
      setAuthLoading(false);
    }
  };

  const checkAndSetUserAccess = async (email, displayName) => {
    if (!email) return;
    
    const localKey = 'lawfirm_firmUsers';
    let localUsers = [];
    try {
      localUsers = JSON.parse(localStorage.getItem(localKey)) || [];
    } catch(e) {}
    
    const combinedUsers = [...firmUsers, ...localUsers];
    
    if (combinedUsers.length === 0) {
      const targetId = 'local_' + Date.now();
      const newUser = { _id: targetId, name: displayName || 'עורך דין ראשי', email: email.toLowerCase(), role: 'admin' };
      await dbOps.handleSave('firmUsers', newUser);
      setCurrentUserProfile(newUser);
      setIsAuthenticated(true);
    } else {
      const existingUser = combinedUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
      if (existingUser) {
        setCurrentUserProfile(existingUser);
        setIsAuthenticated(true);
      } else {
        await signOut(auth);
        await signInAnonymously(auth); 
        setLoginError("הגישה נדחתה: כתובת הדוא\"ל שלך אינה מורשית. אנא פנה למנהל המשרד להוספתך.");
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      if (result.user.email) await checkAndSetUserAccess(result.user.email, result.user.displayName);
      else setLoginError("לא התקבל דוא\"ל מ-Google.");
    } catch (error) {
      console.warn("SSO Popup failed:", error);
      setLoginError("התחברות עם Google נכשלה או בוטלה.");
    }
  };

  const handleAppleLogin = async () => {
    setLoginError('');
    const provider = new OAuthProvider('apple.com');
    try {
      const result = await signInWithPopup(auth, provider);
      if (result.user.email) await checkAndSetUserAccess(result.user.email, result.user.displayName);
      else setLoginError("לא התקבל דוא\"ל מ-Apple.");
    } catch (error) {
      console.warn("SSO Popup failed:", error);
      setLoginError("התחברות עם Apple נכשלה או בוטלה.");
    }
  };

  const handleFacebookLogin = async () => {
    setLoginError('');
    const provider = new FacebookAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      if (result.user.email) await checkAndSetUserAccess(result.user.email, result.user.displayName);
      else setLoginError("לא התקבל דוא\"ל מ-Facebook.");
    } catch (error) {
      console.warn("SSO Popup failed:", error);
      setLoginError("התחברות עם Facebook נכשלה או בוטלה.");
    }
  };

  const getSearchResults = () => {
    if (!globalSearch.trim()) return { cases: [], clients: [] };
    const term = globalSearch.toLowerCase();
    const matchedCases = cases.filter(c => (c.title?.toLowerCase().includes(term) || c.id?.toLowerCase().includes(term)));
    const matchedClients = clients.filter(c => (c.name?.toLowerCase().includes(term) || c.idNumber?.toLowerCase().includes(term)));
    return { cases: matchedCases, clients: matchedClients };
  };

  const handleSearchResultClick = (type, item) => {
    setGlobalSearch('');
    setIsSidebarOpen(false);
    if (type === 'case') {
      setActiveClient(null);
      setActiveCase(item);
    } else if (type === 'client') {
      setActiveCase(null);
      setActiveClient(item);
    }
  };

  const searchResults = getSearchResults();
  const showSearchDropdown = globalSearch.length > 0 && (searchResults.cases.length > 0 || searchResults.clients.length > 0);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 font-sans text-right" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
          <div className="bg-gradient-to-r from-blue-950 to-slate-950 p-8 text-center border-b border-slate-800">
            <Shield className="w-14 h-14 text-blue-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white">מערכת ניהול משרד עו"ד</h1>
            <p className="text-blue-200 text-xs mt-2">גישה מאובטחת לנתוני משרד וניהול תיקים</p>
          </div>
          
          <div className="p-8 space-y-4 bg-slate-950">
            {!isDbReady ? (
              <div className="flex flex-col items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mb-4" />
                <p className="text-slate-400 font-medium">מכין סביבה מאובטחת...</p>
              </div>
            ) : (
              <div className="space-y-4 animate-in fade-in duration-300">
                {loginError && (
                  <div className="text-xs text-red-400 font-medium bg-red-950/50 p-3 rounded-lg border border-red-900/50 mb-2">
                    {loginError}
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-2">
                  <button 
                    onClick={handleGoogleLogin} 
                    className="flex items-center justify-center py-2.5 border border-slate-800 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all shadow-sm hover:scale-105 active:scale-95"
                    title="התחברות עם Google"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </button>
                  
                  <button 
                    onClick={handleAppleLogin} 
                    className="flex items-center justify-center py-2.5 border border-slate-800 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all shadow-sm hover:scale-105 active:scale-95"
                    title="התחברות עם Apple"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm3.307 14.707c-.464.218-.946.335-1.442.348-.962.023-1.517-.584-2.527-.584-1.004 0-1.643.593-2.502.605-.515.011-1.02-.121-1.498-.363-1.637-.828-2.887-3.411-2.046-5.837.404-1.168 1.188-1.933 2.062-1.956.846-.021 1.645.545 2.164.545.522 0 1.455-.688 2.457-.584.421.018.826.115 1.196.286-1.127.674-1.34 2.115-.369 2.973-.655 1.586-1.565 3.39-2.298 3.513zM13.916 6.5c.345.419.553.974.522 1.528-.485.021-1.055-.175-1.428-.61-.351-.41-.595-.986-.546-1.536.522-.023 1.082.191 1.452.618z"/>
                    </svg>
                  </button>
                  
                  <button 
                    onClick={handleFacebookLogin} 
                    className="flex items-center justify-center py-2.5 border border-slate-800 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all shadow-sm hover:scale-105 active:scale-95"
                    title="התחברות עם Facebook"
                  >
                    <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </button>
                </div>

                <div className="relative my-4 flex py-1 items-center">
                  <div className="flex-grow border-t border-slate-800"></div>
                  <span className="flex-shrink mx-4 text-xs font-semibold text-slate-500">או באמצעות דוא"ל</span>
                  <div className="flex-grow border-t border-slate-800"></div>
                </div>

                <form onSubmit={handleEmailAuth} className="space-y-4">
                  {authMode === 'signup' && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1">שם מלא</label>
                      <input 
                        type="text" 
                        required 
                        value={fullNameInput} 
                        onChange={(e) => setFullNameInput(e.target.value)} 
                        className="w-full p-2.5 bg-slate-900 border border-slate-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm" 
                        placeholder="עו&quot;ד ישראל כהן" 
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1">כתובת דוא"ל</label>
                    <input 
                      type="email" 
                      required 
                      value={emailInput} 
                      onChange={(e) => setEmailInput(e.target.value)} 
                      className="w-full p-2.5 bg-slate-900 border border-slate-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm" 
                      placeholder="name@lawfirm.co.il" 
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1">סיסמה</label>
                    <input 
                      type="password" 
                      required 
                      value={passwordInput} 
                      onChange={(e) => setPasswordInput(e.target.value)} 
                      className="w-full p-2.5 bg-slate-900 border border-slate-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm" 
                      placeholder="••••••••" 
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={authLoading}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg font-medium transition-all shadow-md flex items-center justify-center gap-2 text-sm mt-6 hover:scale-[1.01] active:scale-[0.99]"
                  >
                    {authLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : authMode === 'signup' ? (
                      'הרשמה למערכת'
                    ) : (
                      'כניסה למערכת'
                    )}
                  </button>
                </form>

                <div className="text-center mt-6">
                  {authMode === 'signup' ? (
                    <button 
                      onClick={() => { setAuthMode('signin'); setLoginError(''); }} 
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      כבר רשום? לחץ כאן להתחברות
                    </button>
                  ) : (
                    <button 
                      onClick={() => { setAuthMode('signup'); setLoginError(''); }} 
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      משתמש חדש? לחץ כאן להרשמה למערכת
                    </button>
                  )}
                </div>
              </div>
            )}
            <p className="text-[10px] text-center text-slate-600 pt-2 flex items-center justify-center gap-1">
              <Lock className="w-3 h-3" /> חיבור מוצפן SSL מקצה לקצה
            </p>
          </div>
        </div>
      </div>
    );
  }

  const navigateSidebar = (tabName) => {
    setActiveTab(tabName);
    setActiveCase(null);
    setActiveClient(null);
    setIsSidebarOpen(false); 
  };

  const handleOpenNewCaseForClient = (clientName) => {
    setPrefilledClientForCase(clientName);
    setActiveClient(null);
    setActiveTab('cases');
  };

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden" dir="rtl">
      
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/60 z-40 md:hidden backdrop-blur-sm" 
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      <aside className={`fixed inset-y-0 right-0 w-64 bg-blue-950 text-white flex flex-col shadow-xl z-50 transform transition-transform duration-300 md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-4 flex items-center justify-between gap-3 border-b border-blue-900/50">
          <div className="flex items-center gap-3">
             <Shield className="w-8 h-8 text-blue-400 shrink-0" />
             <div>
               <h1 className="font-bold text-lg leading-tight truncate" title={firmSettings.firmName}>{firmSettings.firmName}</h1>
               <p className="text-blue-300 text-xs">משרד עורכי דין</p>
             </div>
          </div>
          <button className="md:hidden text-slate-400 hover:text-white" onClick={() => setIsSidebarOpen(false)}>
             <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="px-4 py-3 bg-blue-900/30 border-b border-blue-900/50 flex items-center gap-2 text-xs text-emerald-400">
          <Lock className="w-3 h-3" />
          <span>חיבור מאובטח - {ROLE_NAMES[currentUserProfile?.role] || currentUserProfile?.role}</span>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <SidebarItem icon={<ShieldCheck />} label="לוח בקרה" active={activeTab === 'dashboard' && !activeCase && !activeClient} onClick={() => navigateSidebar('dashboard')} />
          <SidebarItem icon={<Users />} label="ניהול לקוחות" active={activeTab === 'clients' || !!activeClient} onClick={() => navigateSidebar('clients')} />
          <SidebarItem icon={<Briefcase />} label="ניהול תיקים" active={activeTab === 'cases' || !!activeCase} onClick={() => navigateSidebar('cases')} />
          <SidebarItem icon={<RefreshCw />} label="עריכה והמרת קבצים" active={activeTab === 'converter' && !activeCase && !activeClient} onClick={() => navigateSidebar('converter')} />
          <SidebarItem icon={<Calendar />} label="יומן דיונים" active={activeTab === 'hearings' && !activeCase && !activeClient} onClick={() => navigateSidebar('hearings')} />
          <SidebarItem icon={<CheckSquare />} label="משימות" active={activeTab === 'tasks' && !activeCase && !activeClient} onClick={() => navigateSidebar('tasks')} />

          <SidebarItem icon={<MessageSquare />} label="תקשורת פנימית" badge="0" active={activeTab === 'chat' && !activeCase && !activeClient} onClick={() => navigateSidebar('chat')} />
          <div className="pt-4 mt-4 border-t border-blue-900/50">
            <SidebarItem icon={<Settings />} label="הגדרות המשרד" active={activeTab === 'settings' && !activeCase && !activeClient} onClick={() => navigateSidebar('settings')} />
          </div>
        </nav>

        <div className="p-4 border-t border-blue-900/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-800 rounded-full flex items-center justify-center font-bold text-lg shrink-0">
              {currentUserProfile?.name ? currentUserProfile.name.substring(0, 1) : 'ש'}
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{currentUserProfile?.name}</p>
              <p className="text-blue-400 text-xs truncate">{ROLE_NAMES[currentUserProfile?.role] || currentUserProfile?.role}</p>
            </div>
          </div>
          <button onClick={async () => { 
            await signOut(auth); 
            setIsAuthenticated(false); 
            setCurrentUserProfile(null); 
            await signInAnonymously(auth); 
          }} className="w-full flex items-center gap-2 text-blue-300 hover:text-white hover:bg-blue-900 p-2 rounded-lg transition-colors text-sm">
            <LogOut className="w-4 h-4" /> התנתק בבטחה
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-screen overflow-hidden relative">
        <header className="bg-white shadow-sm border-b border-slate-200 px-4 md:px-8 py-3 md:py-4 flex items-center justify-between z-10 shrink-0">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-slate-500 hover:text-blue-600 p-1" onClick={() => setIsSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="text-lg md:text-xl font-bold text-slate-800 truncate">
              {activeCase ? `תיק: ${activeCase.id || activeCase.title}` : (
                activeClient ? `לקוח: ${activeClient.name}` :
                activeTab === 'dashboard' ? 'לוח בקרה ראשי' :
                activeTab === 'clients' ? 'ניהול לקוחות ושכר טרחה' :
                activeTab === 'cases' ? 'ניהול תיקים משפטיים' :
                activeTab === 'converter' ? 'עריכה והמרת קבצים' :
                activeTab === 'hearings' ? 'יומן דיונים ומעקב' :
                activeTab === 'tasks' ? 'משימות ומעקב יעדים' :
                activeTab === 'users' ? 'ניהול והרשאות משתמשים' :
                activeTab === 'chat' ? 'תקשורת צוות מוצפנת' :
                activeTab === 'settings' ? 'הגדרות ומשתמשים' : ''
              )}
            </h2>
          </div>
          
          <div className="flex items-center gap-3 md:gap-6 shrink-0 relative">
            <div className="relative">
              <Search className="w-4 h-4 md:w-5 md:h-5 absolute right-3 top-2 md:top-2.5 text-slate-400" />
              <input 
                type="text" 
                placeholder="חיפוש כללי..." 
                className="pl-4 pr-9 md:pr-10 py-1.5 md:py-2 bg-slate-100 border border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-full w-36 sm:w-56 md:w-64 text-xs md:text-sm transition-all outline-none" 
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
              />
              
              {showSearchDropdown && (
                <div className="absolute top-full left-0 md:right-0 mt-2 w-64 md:w-80 max-h-96 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-y-auto animate-in fade-in slide-in-from-top-2">
                   {searchResults.cases.length > 0 && (
                      <div className="p-2 border-b border-slate-100">
                         <p className="text-xs font-bold text-slate-400 mb-2 px-2">תיקים משפטיים</p>
                         {searchResults.cases.map(c => (
                            <button key={c._id} onClick={() => handleSearchResultClick('case', c)} className="w-full text-right p-2 hover:bg-blue-50 rounded-lg transition-colors flex flex-col">
                               <span className="font-semibold text-sm text-blue-700">{c.id || 'ללא מספר'}</span>
                               <span className="text-xs text-slate-600 truncate">{c.title}</span>
                            </button>
                         ))}
                      </div>
                   )}
                   {searchResults.clients.length > 0 && (
                      <div className="p-2">
                         <p className="text-xs font-bold text-slate-400 mb-2 px-2">לקוחות</p>
                         {searchResults.clients.map(c => (
                            <button key={c._id} onClick={() => handleSearchResultClick('client', c)} className="w-full text-right p-2 hover:bg-blue-50 rounded-lg transition-colors flex flex-col">
                               <span className="font-semibold text-sm text-slate-800">{c.name}</span>
                               <span className="text-xs text-slate-500">{c.idNumber || c.phone}</span>
                            </button>
                         ))}
                      </div>
                   )}
                </div>
              )}
            </div>
            
            <button className="relative text-slate-500 hover:text-blue-600 transition-colors hidden sm:block">
              <Bell className="w-5 h-5 md:w-6 md:h-6" />
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 md:w-4 md:h-4 bg-red-500 text-white text-[9px] md:text-[10px] font-bold rounded-full flex items-center justify-center">0</span>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto bg-slate-50/50 p-4 md:p-8 relative">
          {activeCase ? (
            <CaseDetailView caseInfo={activeCase} onBack={() => setActiveCase(null)} tasks={tasks} documents={documents} hearings={hearings} firmUsers={firmUsers} currentUserProfile={currentUserProfile} onEditHearing={setEditingHearing} onOpenNetWizard={() => setNetWizardCase(activeCase)} onViewDoc={setViewingDoc} dbOps={dbOps} storage={storage} />
          ) : activeClient ? (
            <ClientDetailView client={activeClient} onBack={() => setActiveClient(null)} cases={cases} documents={documents} dbOps={dbOps} onOpenNewCase={handleOpenNewCaseForClient} onViewDoc={setViewingDoc} storage={storage} currentUserProfile={currentUserProfile} />
          ) : (
            <>
              {activeTab === 'dashboard' && <DashboardView cases={cases} documents={documents} hearings={hearings} tasks={tasks} />}
              {activeTab === 'clients' && <ClientsView clients={clients} onClientSelect={setActiveClient} dbOps={dbOps} globalSearch={globalSearch} onOpenNewCase={handleOpenNewCaseForClient} storage={storage} />}
              {activeTab === 'cases' && <CasesView cases={cases} clients={clients} firmUsers={firmUsers} onOpenNetWizard={setNetWizardCase} onCaseSelect={setActiveCase} dbOps={dbOps} globalSearch={globalSearch} prefilledClient={prefilledClientForCase} clearPrefilledClient={() => setPrefilledClientForCase('')}/>}
              {activeTab === 'converter' && <FileConverterView cases={cases} clients={clients} documents={documents} dbOps={dbOps} storage={storage} currentUserProfile={currentUserProfile} />}
              {activeTab === 'hearings' && <HearingsView hearings={hearings} onEditHearing={setEditingHearing} cases={cases} globalSearch={globalSearch} />}
              {activeTab === 'tasks' && <TasksView tasks={tasks} cases={cases} firmUsers={firmUsers} dbOps={dbOps} globalSearch={globalSearch} />}
              {activeTab === 'chat' && <ChatView currentUserProfile={currentUserProfile} firmUsers={firmUsers} messages={messages} dbOps={dbOps} storage={storage} />}
              {activeTab === 'settings' && (
                <div className="flex flex-col h-full gap-6 animate-in fade-in">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-6 shrink-0">
                    <div className="flex items-center gap-2 mb-4">
                      <Settings className="w-6 h-6 text-slate-500" />
                      <h3 className="font-bold text-lg text-slate-800">הגדרות מערכת</h3>
                    </div>
                    
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 md:p-5 text-right max-w-2xl">
                       <h4 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2"><Briefcase className="w-4 h-4"/> פרטי המשרד</h4>
                       <p className="text-xs text-slate-500 mb-3">עדכן את שם המשרד שיופיע בראש התפריט המערכת (נשמר בענן ומסתנכרן לכלל המשתמשים)</p>
                       <div className="flex flex-col sm:flex-row gap-2">
                         <input 
                           type="text" 
                           className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:border-blue-500 outline-none transition-colors bg-white"
                           value={localFirmName}
                           onChange={(e) => setLocalFirmName(e.target.value)}
                           placeholder="הזן שם משרד עורכי הדין..."
                         />
                         <button 
                           onClick={() => {
                             dbOps.handleSave('settings', { firmName: localFirmName }, firmSettings._id || 'main');
                             alert('שם המשרד עודכן בהצלחה!');
                           }}
                           className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                         >
                           <Save className="w-4 h-4" /> שמור שינויים
                         </button>
                       </div>
                    </div>

                  </div>
                  
                  {(currentUserProfile?.role === 'admin' || currentUserProfile?.role === 'secretary') && (
                    <div className="flex-1 min-h-[400px]">
                      <UserManagementView firmUsers={firmUsers} dbOps={dbOps} currentUserProfile={currentUserProfile} />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {netWizardCase && <NetHamishpatWizard caseInfo={netWizardCase} onClose={() => setNetWizardCase(null)} documents={documents} dbOps={dbOps} />}
      {editingHearing && <HearingEditModal hearing={editingHearing} onClose={() => setEditingHearing(null)} onSave={(savedHearing) => { if (savedHearing._id) { dbOps.handleSave('hearings', savedHearing, savedHearing._id); } else { dbOps.handleSave('hearings', savedHearing); } setEditingHearing(null); }} cases={cases} />}
      {viewingDoc && <DocumentViewerModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />}
      
      <PrivateAIAgent context={{ cases, clients, tasks, documents, dbOps, currentUserProfile }} />
    </div>
  );
}

// --- Components ---
